import './growth-inventory.scss'

import {
  EmployeeInventoryResponseDto,
  InventoryApi,
  InventoryOptionDto
} from '../../metadata/hrnext-performance-service-api'

import { EventBus, GROWTH_INVENTORY_OPEN } from '../../common/event-bus'

import { BaseComponent } from '../../common/base-component'
import { Component } from 'vue-property-decorator'
import { Globals } from '../../common/globals'
import { createApi } from '../../common'

/**
 * Karşılama popup'ı en fazla kaç kez otomatik gösterilecek.
 * Kullanıcı "Atla" dedikten sonra portala her girişinde tekrar gösterilir,
 * bu sayıya ulaşıldığında artık otomatik gösterilmez.
 */
const MAX_AUTO_SHOW_COUNT = 3

interface GrowthInventoryState {
  /** Karşılama popup'ının kaç kez atlandığı/kapatıldığı */
  ignoreCount: number
  /** Envanter tamamlandıysa true; tamamlandığında popup bir daha gösterilmez */
  completed: boolean
}

@Component({
  template: require('./growth-inventory.pug')
})
export class GrowthInventoryComponent extends BaseComponent {
  /** Karşılama (giriş) modalı */
  isWelcomeModalActive = false

  /**
   * Soru modalı. İçeriği (adımlar/sorular) backend'den gelecek;
   * şimdilik sadece iskelet olarak duruyor.
   */
  isQuestionsModalActive = false

  /** Soru modalındaki aktif adım (backend soruları gelince doldurulacak) */
  currentStep = 0

  /** Toplam adım sayısı (ilerleyen adımlar eklendikçe güncellenecek) */
  totalSteps = 5

  /** "Mevcut Profil" adımındaki soru metni; backend'den gelir */
  profileText = ''

  /** Profil metni yüklenirken loading göstergesi */
  isProfileLoading = false

  /** Kullanıcının cevap alanına yazdığı metin */
  answer = ''

  /** Cevap alanı için karakter sınırı */
  answerMaxLength = 250

  /** Vizyon adımı seçenekleri; backend'den (InventoryVision/ListTranslated) gelir */
  visionOptions: InventoryOptionDto[] = []

  /** Vizyon seçenekleri yüklenirken loading göstergesi */
  isVisionLoading = false

  /** Seçili vizyon seçeneğinin id'si */
  selectedVisionId: string | null = null

  /**
   * Her vizyon seçeneği için opsiyonel açıklama metni. Seçenek id'si -> metin.
   * Seçenek seçildiğinde altında açılan 50 karakterlik alana yazılır.
   */
  visionTexts: Record<string, string> = {}

  /** Vizyon seçeneklerindeki opsiyonel açıklama alanı için karakter sınırı */
  visionTextMaxLength = 50

  /** Odak adımı seçenekleri; backend'den (InventoryFocus/ListTranslated) gelir */
  focusOptions: InventoryOptionDto[] = []

  /** Odak seçenekleri yüklenirken loading göstergesi */
  isFocusLoading = false

  /** Seçili odak seçeneğinin id'si */
  selectedFocusId: string | null = null

  /**
   * Her odak seçeneği için opsiyonel açıklama metni. Seçenek id'si -> metin.
   * Seçenek seçildiğinde altında açılan 50 karakterlik alana yazılır.
   */
  focusTexts: Record<string, string> = {}

  /** Odak seçeneklerindeki opsiyonel açıklama alanı için karakter sınırı */
  focusTextMaxLength = 50

  /** Motivasyon adımı seçenekleri; backend'den (InventoryMotivation/ListTranslated) gelir */
  motivationOptions: InventoryOptionDto[] = []

  /** Motivasyon seçenekleri yüklenirken loading göstergesi */
  isMotivationLoading = false

  /** Seçili motivasyon seçeneğinin id'si */
  selectedMotivationId: string | null = null

  /**
   * Her motivasyon seçeneği için opsiyonel açıklama metni. Seçenek id'si -> metin.
   * Seçenek seçildiğinde altında açılan 50 karakterlik alana yazılır.
   */
  motivationTexts: Record<string, string> = {}

  /** Motivasyon seçeneklerindeki opsiyonel açıklama alanı için karakter sınırı */
  motivationTextMaxLength = 50

  /** Gelişim adımı seçenekleri; backend'den (InventoryDevelopment/ListTranslated) gelir */
  developmentOptions: InventoryOptionDto[] = []

  /** Gelişim seçenekleri yüklenirken loading göstergesi */
  isDevelopmentLoading = false

  /** Seçili gelişim seçeneğinin id'si */
  selectedDevelopmentId: string | null = null

  /**
   * Her gelişim seçeneği için opsiyonel açıklama metni. Seçenek id'si -> metin.
   * Seçenek seçildiğinde altında açılan 50 karakterlik alana yazılır.
   */
  developmentTexts: Record<string, string> = {}

  /** Gelişim seçeneklerindeki opsiyonel açıklama alanı için karakter sınırı */
  developmentTextMaxLength = 50

  /** Bariyer adımı seçenekleri; backend'den (InventoryBarrier/ListTranslated) gelir */
  barrierOptions: InventoryOptionDto[] = []

  /** Bariyer seçenekleri yüklenirken loading göstergesi */
  isBarrierLoading = false

  /** Seçili bariyer seçeneğinin id'si */
  selectedBarrierId: string | null = null

  /**
   * Her bariyer seçeneği için opsiyonel açıklama metni. Seçenek id'si -> metin.
   * Seçenek seçildiğinde altında açılan 50 karakterlik alana yazılır.
   */
  barrierTexts: Record<string, string> = {}

  /** Bariyer seçeneklerindeki opsiyonel açıklama alanı için karakter sınırı */
  barrierTextMaxLength = 50

  /** Bariyer adımının index'i — bu adımdaki "İleri" cevapları kaydeder */
  barrierStepIndex = 5

  /** Son adımın index'i (içerik adımları 0..lastStepIndex arası ilerler) — Aktivasyon */
  lastStepIndex = 6

  /** Cevaplar kaydedilirken loading göstergesi (Aktivasyon adımı) */
  isSaving = false

  constructor() {
    super()
  }

  /** localStorage anahtarını kullanıcı bazında tutuyoruz */
  get storageKey(): string {
    const userId = this.userInfo?.CompanyEmployeeId ?? this.currentUserId ?? 'anonymous'
    return `growth-inventory-${userId}`
  }

  get state(): GrowthInventoryState {
    return (
      (Globals.getStorageItem(this.storageKey, true) as GrowthInventoryState) ?? {
        ignoreCount: 0,
        completed: false
      }
    )
  }

  saveState(state: GrowthInventoryState): void {
    // ttl=null => süresiz; useLocalStorage=true
    Globals.setStorageItem(this.storageKey, state, null, true)
  }

  private async syncStateFromBackend(): Promise<void> {
    // localStorage'da zaten veri varsa senkronizasyon gerekmez
    const existingState = Globals.getStorageItem(this.storageKey, true) as GrowthInventoryState | null
    if (existingState != null) return

    try {
      const api = createApi(InventoryApi)
      const config = await api.getCurrentConfig()
      if (!config) return

      this.saveState({
        ignoreCount: config.isDismissed ? MAX_AUTO_SHOW_COUNT : config.ignoreCount ?? 0,
        completed: config.isCompleted ?? false
      })
    } catch {
      // Backend erişilemezse localStorage varsayılanları (ignoreCount:0, completed:false) kullanılır
    }
  }

  async created(): Promise<void> {
    await super.created()
    if (!Globals.isFeatureAIAllowed) return
    // Her yerden açılabilmesi için global event'i dinle
    EventBus.$on(GROWTH_INVENTORY_OPEN, this.open)
    await this.syncStateFromBackend()
    this.evaluateAutoShow()
  }

  beforeDestroy(): void {
    EventBus.$off(GROWTH_INVENTORY_OPEN, this.open)
  }

  /** Portala girişte popup'ın gösterilip gösterilmeyeceğine karar verir */
  evaluateAutoShow(): void {
    const state = this.state

    if (state.completed) return
    if (state.ignoreCount >= MAX_AUTO_SHOW_COUNT) return

    // Sayaç burada artmaz; yalnızca "Atla"/"X" ile kapatınca artırılır.
    this.isWelcomeModalActive = true
  }

  /**
   * Popup'a başka bir yerden (ör. menü/buton) erişilebilmesi için public giriş noktası.
   * Otomatik gösterim sayacından bağımsız olarak modalı açar.
   * Event bus üzerinden de çağrıldığı için `this` bağlı kalsın diye arrow function.
   */
  open = (): void => {
    this.isWelcomeModalActive = true
  }

  /** "Atla" veya "X" ile kapatma: gösterim sayacını artırır ve modalları kapatır */
  async handleSkip(): Promise<void> {
    const state = this.state
    if (!state.completed && state.ignoreCount < MAX_AUTO_SHOW_COUNT) {
      this.saveState({ ...state, ignoreCount: state.ignoreCount + 1 })
    }
    this.isWelcomeModalActive = false
    this.isQuestionsModalActive = false
    try {
      const api = createApi(InventoryApi)
      await api.incrementIgnoreCount()
    } catch {
      // sayaç artırma kritik değil; hata sessizce geçilir
    }
  }

  async handleStart(): Promise<void> {
    this.isWelcomeModalActive = false
    this.currentStep = 0
    this.isQuestionsModalActive = true
    await this.loadProfileInfo()
  }

  /** "Mevcut Profil" adımındaki soru metnini backend'den getirir */
  async loadProfileInfo(): Promise<void> {
    if (this.profileText) return
    this.isProfileLoading = true
    try {
      const api = createApi(InventoryApi)
      this.profileText = (await api.getEmployeeProfileInfo()) ?? ''
    } finally {
      this.isProfileLoading = false
    }
  }

  /** "Vizyon" adımındaki seçenekleri backend'den getirir */
  async loadVisionOptions(): Promise<void> {
    if (this.visionOptions.length) return
    this.isVisionLoading = true
    try {
      const api = createApi(InventoryApi)
      const options: InventoryOptionDto[] = await api.listVisionTranslated().toArrayAsync()
      this.visionOptions = this.sortSpecialOptionsToEnd(options ?? [])
      // Her seçenek için opsiyonel metni baştan reactive olacak şekilde hazırla
      this.visionTexts = this.visionOptions.reduce((acc, option) => {
        if (option.id) acc[option.id] = ''
        return acc
      }, {} as Record<string, string>)
    } finally {
      this.isVisionLoading = false
    }
  }

  /** "Odak" adımındaki seçenekleri backend'den getirir */
  async loadFocusOptions(): Promise<void> {
    if (this.focusOptions.length) return
    this.isFocusLoading = true
    try {
      const api = createApi(InventoryApi)
      const options: InventoryOptionDto[] = await api.listFocusTranslated().toArrayAsync()
      this.focusOptions = this.sortSpecialOptionsToEnd(options ?? [])
      // Her seçenek için opsiyonel metni baştan reactive olacak şekilde hazırla
      this.focusTexts = this.focusOptions.reduce((acc, option) => {
        if (option.id) acc[option.id] = ''
        return acc
      }, {} as Record<string, string>)
    } finally {
      this.isFocusLoading = false
    }
  }

  /** "Motivasyon" adımındaki seçenekleri backend'den getirir */
  async loadMotivationOptions(): Promise<void> {
    if (this.motivationOptions.length) return
    this.isMotivationLoading = true
    try {
      const api = createApi(InventoryApi)
      const options: InventoryOptionDto[] = await api.listMotivationTranslated().toArrayAsync()
      this.motivationOptions = this.sortSpecialOptionsToEnd(options ?? [])
      // Her seçenek için opsiyonel metni baştan reactive olacak şekilde hazırla
      this.motivationTexts = this.motivationOptions.reduce((acc, option) => {
        if (option.id) acc[option.id] = ''
        return acc
      }, {} as Record<string, string>)
    } finally {
      this.isMotivationLoading = false
    }
  }

  /** "Gelişim" adımındaki seçenekleri backend'den getirir */
  async loadDevelopmentOptions(): Promise<void> {
    if (this.developmentOptions.length) return
    this.isDevelopmentLoading = true
    try {
      const api = createApi(InventoryApi)
      const options: InventoryOptionDto[] = await api.listDevelopmentTranslated().toArrayAsync()
      this.developmentOptions = this.sortSpecialOptionsToEnd(options ?? [])
      // Her seçenek için opsiyonel metni baştan reactive olacak şekilde hazırla
      this.developmentTexts = this.developmentOptions.reduce((acc, option) => {
        if (option.id) acc[option.id] = ''
        return acc
      }, {} as Record<string, string>)
    } finally {
      this.isDevelopmentLoading = false
    }
  }

  /** "Bariyer" adımındaki seçenekleri backend'den getirir */
  async loadBarrierOptions(): Promise<void> {
    if (this.barrierOptions.length) return
    this.isBarrierLoading = true
    try {
      const api = createApi(InventoryApi)
      const options: InventoryOptionDto[] = await api.listBarrierTranslated().toArrayAsync()
      this.barrierOptions = this.sortSpecialOptionsToEnd(options ?? [])
      // Her seçenek için opsiyonel metni baştan reactive olacak şekilde hazırla
      this.barrierTexts = this.barrierOptions.reduce((acc, option) => {
        if (option.id) acc[option.id] = ''
        return acc
      }, {} as Record<string, string>)
    } finally {
      this.isBarrierLoading = false
    }
  }

  /**
   * "Kararsızım" ve "Diğer" gibi seçenekleri her zaman listenin en altına iter.
   * Bu seçenekler için backend'de ayrı bir alan olmadığından isimden (çok dilli)
   * tespit edilir. Sıralama kararlıdır: normal seçenekler backend sırasını korur,
   * en altta önce "Kararsızım", en sonda "Diğer" gelir.
   */
  private specialOptionRank(option: InventoryOptionDto): number {
    const name = (option.name || '').toLocaleLowerCase('tr-TR')
    const undecidedNeedles = ['kararsız', 'kararsiz', 'undecided', 'indécis', 'indecis', 'indeciso']
    if (undecidedNeedles.some(needle => name.includes(needle))) return 1
    const otherNeedles = ['diğer', 'diger', 'other', 'autre', 'otro']
    if (otherNeedles.some(needle => name.includes(needle))) return 2
    return 0
  }

  sortSpecialOptionsToEnd(options: InventoryOptionDto[]): InventoryOptionDto[] {
    // Array.prototype.sort kararlıdır; aynı rank içindeki sıra korunur.
    return [...options].sort((a, b) => this.specialOptionRank(a) - this.specialOptionRank(b))
  }

  /**
   * Bir seçeneğin altındaki opsiyonel metin alanı için placeholder döndürür.
   * Cevaba (seçenek adına) özel bir metin tanımlıysa onu, yoksa genel placeholder'ı kullanır.
   * @param group i18n placeholder grubu: 'visionPlaceholders' | 'focusPlaceholders'
   */
  optionPlaceholder(group: string, option: InventoryOptionDto): string {
    const key = `components.growthInventory.${group}.${option.name}`
    if (option.name && this.$te(key)) {
      return this.$t(key) as string
    }
    return this.$t('components.growthInventory.optionDetailPlaceholder') as string
  }

  async handleQuestionsClose(): Promise<void> {
    this.isQuestionsModalActive = false
    const state = this.state
    if (!state.completed && state.ignoreCount < MAX_AUTO_SHOW_COUNT) {
      this.saveState({ ...state, ignoreCount: state.ignoreCount + 1 })
    }
    try {
      const api = createApi(InventoryApi)
      await api.incrementIgnoreCount()
    } catch {
      // sayaç artırma kritik değil; hata sessizce geçilir
    }
  }

  handleBack(): void {
    if (this.currentStep > 0) {
      this.currentStep--
      return
    }
    // İlk adımda "Geri" dendiğinde karşılama modalına dönülür
    this.isQuestionsModalActive = false
    this.isWelcomeModalActive = true
  }

  async handleNext(): Promise<void> {
    if (this.currentStep < this.lastStepIndex) {
      this.currentStep++
    }
    // Vizyon adımına geçildiğinde seçenekleri yükle
    if (this.currentStep === 1) {
      await this.loadVisionOptions()
    }
    // Odak adımına geçildiğinde seçenekleri yükle
    if (this.currentStep === 2) {
      await this.loadFocusOptions()
    }
    // Motivasyon adımına geçildiğinde seçenekleri yükle
    if (this.currentStep === 3) {
      await this.loadMotivationOptions()
    }
    // Gelişim adımına geçildiğinde seçenekleri yükle
    if (this.currentStep === 4) {
      await this.loadDevelopmentOptions()
    }
    // Bariyer adımına geçildiğinde seçenekleri yükle
    if (this.currentStep === 5) {
      await this.loadBarrierOptions()
    }
  }

  /**
   * Seçili bir seçeneğin altındaki opsiyonel açıklama metnini döndürür.
   * Boş ise undefined döner (opsiyonel olduğu için payload'a eklenmez).
   */
  selectedDescription(texts: Record<string, string>, selectedId: string | null): string | undefined {
    if (!selectedId) return undefined
    const text = (texts[selectedId] || '').trim()
    return text ? text : undefined
  }

  /**
   * Bariyer adımındaki "İleri": tüm cevapları backend'e kaydeder, envanteri tamamlar
   * ve Aktivasyon (özet) adımına geçer. Kayıt başarısız olursa adım ilerlemez.
   */
  async handleSaveAndReview(): Promise<void> {
    this.isSaving = true
    try {
      const api = createApi(InventoryApi)
      const dto: EmployeeInventoryResponseDto = {
        employeeId: this.userInfo?.CompanyEmployeeId,
        description: this.answer ? this.answer.trim() : undefined,
        inventoryVisionId: this.selectedVisionId ?? undefined,
        inventoryVisionDescription: this.selectedDescription(this.visionTexts, this.selectedVisionId),
        inventoryFocusId: this.selectedFocusId ?? undefined,
        inventoryFocusDescription: this.selectedDescription(this.focusTexts, this.selectedFocusId),
        inventoryMotivationId: this.selectedMotivationId ?? undefined,
        inventoryMotivationDescription: this.selectedDescription(this.motivationTexts, this.selectedMotivationId),
        inventoryDevelopmentId: this.selectedDevelopmentId ?? undefined,
        inventoryDevelopmentDescription: this.selectedDescription(this.developmentTexts, this.selectedDevelopmentId),
        inventoryBarrierId: this.selectedBarrierId ?? undefined,
        inventoryBarrierDescription: this.selectedDescription(this.barrierTexts, this.selectedBarrierId),
        responseDate: new Date()
      }
      await api.saveResponse(dto)
      // Tamamlandı olarak işaretle; popup bir daha otomatik açılmasın ve
      // "Atla/X" sayacı artık işlemesin (envanter bitirilmiş sayılır)
      this.saveState({ ...this.state, completed: true })
      // Aktivasyon (özet) adımına geç
      this.currentStep = this.lastStepIndex
    } finally {
      this.isSaving = false
    }
  }

  /**
   * Aktivasyon adımındaki CTA. Envanter Bariyer adımında zaten kaydedildiği için
   * burada yalnızca modal kapatılır; sayaç artmaz (completed = true).
   */
  handleFinish(): void {
    this.isQuestionsModalActive = false
  }
}
